import type { InputState } from './InputTypes'
import { getSensitivityFactor } from './sensitivity'

export class TouchInput {
  private static readonly PERF_ALPHA = 0.12
  private active = false
  private activeTouchId: number | null = null
  private originX = 0
  private originY = 0
  private currentX = 0
  private currentY = 0
  private boosting = false
  private lastTapTime = 0
  private lastTouchStartAtMs = 0
  private lastTouchMoveAtMs = 0
  private staleRecoveryCount = 0
  private avgTouchMoveGapMs = 0
  private disposed = false

  private readonly DOUBLE_TAP_THRESHOLD = 300
  private readonly DEADZONE_PX = 18

  private readonly onTouchStart: (e: TouchEvent) => void
  private readonly onTouchMove: (e: TouchEvent) => void
  private readonly onTouchEnd: (e: TouchEvent) => void
  private readonly onTouchCancel: (e: TouchEvent) => void
  private readonly onWindowBlur: () => void
  private readonly onVisibilityChange: () => void

  constructor(
    private canvas: HTMLCanvasElement,
  ) {
    this.onTouchStart = (e: TouchEvent): void => {
      if (this.disposed) return
      if (this.isInteractiveTarget(e.target)) return
      if (this.activeTouchId !== null) {
        const existingTouch = this.findTrackedTouch(e.touches)
        if (existingTouch) return
        this.staleRecoveryCount++
        this.resetActiveTouch()
      }
      const touch = e.changedTouches[0]
      if (!touch) return

      e.preventDefault()
      const nowMs = performance.now()
      this.activeTouchId = touch.identifier
      this.active = true
      this.originX = touch.clientX
      this.originY = touch.clientY
      this.currentX = touch.clientX
      this.currentY = touch.clientY
      this.lastTouchStartAtMs = nowMs
      this.lastTouchMoveAtMs = nowMs

      const now = Date.now()
      if (now - this.lastTapTime < this.DOUBLE_TAP_THRESHOLD) {
        this.boosting = true
      }
      this.lastTapTime = now
    }

    this.onTouchMove = (e: TouchEvent): void => {
      if (!this.active || this.activeTouchId === null) return
      const touch = this.findTrackedTouch(e.changedTouches)
      if (!touch) return

      e.preventDefault()
      const nowMs = performance.now()
      if (this.lastTouchMoveAtMs > 0) {
        const gapMs = Math.max(0, nowMs - this.lastTouchMoveAtMs)
        this.avgTouchMoveGapMs = this.avgTouchMoveGapMs === 0
          ? gapMs
          : this.avgTouchMoveGapMs * (1 - TouchInput.PERF_ALPHA) + gapMs * TouchInput.PERF_ALPHA
      }
      this.currentX = touch.clientX
      this.currentY = touch.clientY
      this.lastTouchMoveAtMs = nowMs
    }

    this.onTouchEnd = (e: TouchEvent): void => {
      if (this.activeTouchId === null) return
      const touch = this.findTrackedTouch(e.changedTouches)
      if (!touch) return

      e.preventDefault()
      this.resetActiveTouch()
    }

    this.onTouchCancel = (e: TouchEvent): void => {
      if (this.activeTouchId === null) return
      const touch = this.findTrackedTouch(e.changedTouches)
      if (!touch) return

      e.preventDefault()
      this.resetActiveTouch()
    }

    this.onWindowBlur = (): void => {
      this.resetActiveTouch()
    }

    this.onVisibilityChange = (): void => {
      if (document.hidden) {
        this.resetActiveTouch()
      }
    }

    this.bind()
  }

  private bind(): void {
    const tg = window.Telegram?.WebApp as (
      | {
        disableVerticalSwipes?: () => void
        setupSwipeBehavior?: (options: { allow_vertical_swipe: boolean }) => void
      }
      | undefined
    )

    tg?.disableVerticalSwipes?.()
    tg?.setupSwipeBehavior?.({ allow_vertical_swipe: false })

    document.documentElement.style.touchAction = 'none'
    document.documentElement.style.overscrollBehavior = 'none'
    document.body.style.touchAction = 'none'
    document.body.style.overscrollBehavior = 'none'
    this.canvas.style.touchAction = 'none'

    window.addEventListener('touchstart', this.onTouchStart, { passive: false, capture: true })
    window.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true })
    window.addEventListener('touchend', this.onTouchEnd, { passive: false, capture: true })
    window.addEventListener('touchcancel', this.onTouchCancel, { passive: false, capture: true })
    window.addEventListener('blur', this.onWindowBlur)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  getState(): InputState {
    const factor = getSensitivityFactor()
    if (!this.active) {
      return {
        desiredHeading: 0,
        boost: this.boosting,
        hasAim: false,
        strength: 0,
        distancePx: 0,
      }
    }

    const dx = (this.currentX - this.originX) * factor
    const dy = (this.currentY - this.originY) * factor
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance <= this.DEADZONE_PX) {
      return {
        desiredHeading: 0,
        boost: this.boosting,
        hasAim: false,
        strength: 0,
        distancePx: distance,
      }
    }
    return {
      desiredHeading: Math.atan2(dy, dx),
      boost: this.boosting,
      hasAim: true,
      strength: Math.min(1, distance / 120),
      distancePx: distance,
    }
  }

  isActive(): boolean {
    return this.active
  }

  getActiveTouchAgeMs(nowMs = performance.now()): number | null {
    if (!this.active || this.lastTouchStartAtMs <= 0) return null
    return Math.max(0, nowMs - this.lastTouchStartAtMs)
  }

  getLastMoveAgeMs(nowMs = performance.now()): number | null {
    if (!this.active || this.lastTouchMoveAtMs <= 0) return null
    return Math.max(0, nowMs - this.lastTouchMoveAtMs)
  }

  getStaleRecoveryCount(): number {
    return this.staleRecoveryCount
  }

  getAverageMoveGapMs(): number | null {
    return this.avgTouchMoveGapMs > 0 ? this.avgTouchMoveGapMs : null
  }

  getOrigin(): { x: number; y: number } {
    return { x: this.originX, y: this.originY }
  }

  getThumbPosition(): { x: number; y: number } {
    return { x: this.currentX, y: this.currentY }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true

    if (window.Telegram?.WebApp?.enableVerticalSwipes) {
      window.Telegram.WebApp.enableVerticalSwipes()
    }

    document.documentElement.style.touchAction = ''
    document.documentElement.style.overscrollBehavior = ''
    document.body.style.touchAction = ''
    document.body.style.overscrollBehavior = ''
    this.canvas.style.touchAction = ''

    window.removeEventListener('touchstart', this.onTouchStart, true)
    window.removeEventListener('touchmove', this.onTouchMove, true)
    window.removeEventListener('touchend', this.onTouchEnd, true)
    window.removeEventListener('touchcancel', this.onTouchCancel, true)
    window.removeEventListener('blur', this.onWindowBlur)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  private findTrackedTouch(touches: TouchList): Touch | null {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i)
      if (touch && touch.identifier === this.activeTouchId) return touch
    }
    return null
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false
    return Boolean(target.closest(
      'button, a, input, textarea, select, option, summary, label, [role="button"], [data-touch-ignore], [contenteditable="true"]',
    ))
  }

  private resetActiveTouch(): void {
    this.active = false
    this.activeTouchId = null
    this.boosting = false
    this.lastTouchStartAtMs = 0
    this.lastTouchMoveAtMs = 0
  }
}
